import { useCallback, useMemo, useRef, useState, type ChangeEvent, type MouseEvent, type ReactNode, type UIEvent } from 'react'
import { useIntl } from 'react-intl'
import styled, { type DefaultTheme } from 'styled-components'
import { rem } from 'polished'

import FormatCurrency from 'components/Common/FormatCurrency'
import Group from 'components/utils/Group'
import { countByAsRecord } from 'lib/array/arrayUtils'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import TextButton from 'LuxKit/Button/TextButton'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import Switch from 'LuxKit/Switch/Switch'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import LuxPlusLogoWrapper from 'luxPlus/components/LuxPlusLogoWrapper'
import type { TripStop } from 'tripPlanner/api/types'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineArrowDownIcon from 'LuxKit/Icons/line/LineArrowDownIcon'
import LineArrowUpIcon from 'LuxKit/Icons/line/LineArrowUpIcon'
import LineSortingIcon from 'LuxKit/Icons/line/LineSortingIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import LocationFilterMenuItem from './LocationFilterMenuItem'
import TripListRowItem from './TripListRowItem'
import StatusFilterMenuItem from './StatusFilterMenuItem'
import TypeFilterMenuItem from './TypeFilterMenuItem'
import {
  canMoveTripListRowToAnotherDay,
  getTripListBulkMoveDayOptions,
  type TripListMoveDayOption,
} from './tripListMoveDayOptions'
import TripListQuickActions from './TripListQuickActions'
import { TRIP_LIST_GRID_COLUMNS } from './tripListLayout'
import { getTripListTypeLabel } from './tripListTypeLabels'
import TripListCheckboxButton from './TripListCheckboxButton'
import { getTripListFooterTotalPrice } from './tripListPrices'
import { getTripListStatusLabel } from './tripListStatus'
import type { ItineraryScrollTarget } from '../../hooks/useItineraryUpdates'
import { useItineraryScrollTarget } from '../../hooks/useItineraryScrollTarget'
import type {
  TripListFilters,
  TripListGroup,
  TripListLocation,
  TripListRow,
  TripListRowType,
  TripListSort,
  TripListSortField,
  TripListStatusValue,
} from './tripListTypes'

const TYPE_FILTERS: ReadonlyArray<TripListRowType> = [
  'flight',
  'accommodation',
  'experience',
  'cruise',
  'activity',
  'dining',
  'transport',
]

const STATUS_FILTERS: ReadonlyArray<TripListStatusValue> = ['IDEA', 'BOOKED', 'NOT_BOOKED']

const HEADER_STICKY_SCROLL_THRESHOLD = 60

const TRIP_LIST_SCROLL_SELECTORS = {
  item: (target: ItineraryScrollTarget) => target.itemId !== undefined ? `[data-item-id="${target.itemId}"]` : undefined,
  day: (target: ItineraryScrollTarget) => target.dayId !== undefined ? `[data-day-id="${target.dayId}"]` : undefined,
  stop: (target: ItineraryScrollTarget) => `[data-stop-id="${target.stopId}"]`,
}

interface LocationFilterOption {
  location: TripListLocation
  count: number
  firstIndex: number
}

interface TripListRenderableRow {
  row: TripListRow
  showGroupMetadata: boolean
  firstRow: boolean
  lastRow: boolean
  selected: boolean
  isBookingPending: boolean
  isNoteUpdatePending: boolean
  isStatusUpdatePending: boolean
}

interface TripListRenderableGroup {
  id: string
  rows: Array<TripListRenderableRow>
}

const ListPanel = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
`

const Toolbar = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: ${rem(1040)};
  padding: ${rem(16)} ${rem(20)} ${rem(12)};
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
  flex-shrink: 0;
`

const ToolbarControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  border-radius: ${props => props.theme.borderRadius.L};
`

const LuxPlusToggle = styled.label`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  height: ${rem(32)};
  padding: 0 ${rem(10)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  border-radius: ${props => props.theme.borderRadius.round};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const ClearFiltersButton = styled(TextButton)`
  && {
    height: ${rem(32)};
    padding-right: ${rem(10)};
    padding-left: ${rem(10)};
    border-radius: ${props => props.theme.borderRadius.round};
  }
`

const ScrollArea = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  overflow: auto;
  padding: 0 ${rem(20)} ${rem(12)};
`

const HeaderRow = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: ${TRIP_LIST_GRID_COLUMNS};
  align-items: center;
  min-width: ${rem(1040)};
  min-height: ${rem(48)};
  margin-bottom: ${rem(8)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.L};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  color: ${props => props.theme.palette.neutral.surface.normalContrast};

  &.is-stuck {
    min-width: calc(${rem(1040)} + ${rem(40)});
    min-height: ${rem(48)};
    margin-right: ${rem(-20)};
    margin-left: ${rem(-20)};
    padding-right: ${rem(20)};
    padding-left: ${rem(20)};
    border-right: 0;
    border-left: 0;
    border-radius: 0;
    box-shadow: 0 ${rem(8)} ${rem(16)} rgba(0, 0, 0, 0.08);
  }
`

const HeaderCell = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(4)};
  min-width: 0;
  padding: ${rem(12)} ${rem(8)};

  &:first-child {
    justify-content: center;
  }
`

const HeaderCheckboxCell = styled(HeaderCell)`
  cursor: pointer;
`

const headerIconBackground = (props: { theme: DefaultTheme }) => props.theme.palette.neutral.surface.lightBackground

const HeaderButton = styled(TextButton)`
  && {
    display: flex;
    align-items: center;
    gap: ${rem(4)};
    min-width: 0;
    width: ${rem(24)};
    height: ${rem(24)};
    padding: 0;
    border: 0;
    border-radius: ${props => props.theme.borderRadius.round};
    background: transparent;
    color: inherit;
    cursor: pointer;
    --button-color: inherit;
    --button-background-color: transparent;
    --button-border-color: transparent;
    --button-hover-color: inherit;
    --button-hover-background-color: ${headerIconBackground};
    --button-hover-border-color: transparent;
    --button-active-color: inherit;
    --button-active-background-color: ${headerIconBackground};
    --button-active-border-color: transparent;
    --button-focus-color: inherit;
    --button-focus-background-color: transparent;
    --button-focus-border-color: transparent;

    &:hover,
    &:active {
      background-color: ${headerIconBackground};
      color: inherit;
    }
  }
`

const FilterTriggerButton = styled(HeaderButton)`
  &&.is-active {
    gap: ${rem(2)};
    width: auto;
    min-width: ${rem(24)};
    padding: 0 ${rem(6)};
    border: 0;
    background: ${headerIconBackground};
    color: ${props => props.theme.palette.neutral.surface.normalContrast};
    --button-background-color: ${headerIconBackground};
    --button-border-color: transparent;
    --button-color: ${props => props.theme.palette.neutral.surface.normalContrast};
    --button-hover-background-color: ${headerIconBackground};
    --button-hover-border-color: transparent;
    --button-hover-color: ${props => props.theme.palette.neutral.surface.normalContrast};
    --button-active-background-color: ${headerIconBackground};
    --button-active-border-color: transparent;
    --button-active-color: ${props => props.theme.palette.neutral.surface.normalContrast};
    --button-focus-background-color: ${headerIconBackground};
    --button-focus-border-color: transparent;
    --button-focus-color: ${props => props.theme.palette.neutral.surface.normalContrast};

    &:hover,
    &:active {
      background-color: ${headerIconBackground};
      color: ${props => props.theme.palette.neutral.surface.normalContrast};
    }
  }
`

const FilterDropdownList = styled(DropdownList)`
  > div > div {
    padding-bottom: 0;
  }
`

const TypeFilterPanel = styled.div`
  display: flex;
  flex-direction: column;
  width: ${rem(256)};
  max-height: ${rem(360)};
`

const TypeFilterHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  padding: ${rem(4)} ${rem(16)} ${rem(2)};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  flex-shrink: 0;
`

const TypeFilterContent = styled.div`
  overflow-y: auto;
  padding: 0 ${rem(16)};
`

const TypeFilterOptions = styled.div`
  margin-top: ${rem(4)};
`

const TypeFilterFooter = styled.div`
  position: sticky;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${rem(16)};
  padding: ${rem(16)} ${rem(16)} 0;
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  flex-shrink: 0;
`

const ClearFilterButton = styled(TextButton)`
  min-width: 0;
  padding-right: 0;
  padding-left: 0;
`

const ContentStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  min-width: ${rem(1040)};
`

const DayGroup = styled.section`
  background: ${props => props.theme.palette.neutral.surface.normalBackground};

  &:first-child {
    border-top-left-radius: ${props => props.theme.borderRadius.L};
    border-top-right-radius: ${props => props.theme.borderRadius.L};
  }

  &:last-child {
    border-bottom-right-radius: ${props => props.theme.borderRadius.L};
    border-bottom-left-radius: ${props => props.theme.borderRadius.L};
  }
`

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(16)};
  margin: 0;
  padding: ${rem(16)} ${rem(20)};
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  min-width: 0;
  flex-shrink: 0;
`

const FooterSummary = styled(Group)`
  min-width: 0;
`

const FooterActions = styled(Group)`
  flex-shrink: 0;
`

const CountChip = styled.span`
  padding: ${rem(2)} ${rem(8)};
  border-radius: ${props => props.theme.borderRadius.round};
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
`

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: ${rem(200)};
  padding: ${rem(24)};
`

const BookAllButton = styled(TextButton)`
  border-radius: ${props => props.theme.borderRadius.round};
`

interface Props {
  groups: Array<TripListGroup>
  tripStops: Array<TripStop>
  allRows: Array<TripListRow>
  visibleRows: Array<TripListRow>
  selectedRows: Array<TripListRow>
  selectedRowIds: Set<string>
  filters: TripListFilters
  sort: TripListSort | undefined
  luxPlusEnabled: boolean
  onSelectAll: (checked: boolean) => void
  onSelectRow: (rowId: string, checked: boolean) => void
  onLocationFiltersChange: (locations: Set<string>) => void
  onTypeFiltersChange: (types: Set<TripListRowType>, bookableOnly: boolean) => void
  onStatusFiltersChange: (statuses: Set<TripListStatusValue>) => void
  onClearFilters: () => void
  onSortToggle: (field: TripListSortField) => void
  onLuxPlusToggle: () => void
  onRowClick: (row: TripListRow) => void
  onBookRow: (row: TripListRow) => void
  onBookAll: () => void
  onViewBooking: (row: TripListRow) => void
  onAskAi: (row: TripListRow, origin?: { x: number; y: number }) => void
  onAskAiMove: (row: TripListRow, origin?: { x: number; y: number }) => void
  onAskAiSelectedRows: (origin?: { x: number; y: number }) => void
  onAskAiMoveSelectedRows: (origin?: { x: number; y: number }) => void
  onDelete: (row: TripListRow) => void
  onMoveToDay: (row: TripListRow, option: TripListMoveDayOption) => void
  onMoveSelectedRowsToDay: (option: TripListMoveDayOption) => void | Promise<void>
  onDeleteSelectedRows: () => Promise<void>
  onClearSelection: () => void
  onUpdateNote: (row: TripListRow, notes: string | undefined) => void
  onUpdateStatus: (row: TripListRow, status: TripListStatusValue) => void
  bookingRowId?: string
  noteUpdateRowId?: string
  statusUpdateRowId?: string
  notesDisabled?: boolean
  statusDisabled?: boolean
  actionsDisabled?: boolean
  scrollTarget?: ItineraryScrollTarget
  onScrollComplete?: () => void
}

function getSortIcon(sort: TripListSort | undefined, field: TripListSortField) {
  if (sort?.field !== field) return <LineSortingIcon size="XS" />
  return sort.direction === 'asc' ? <LineArrowUpIcon size="XS" /> : <LineArrowDownIcon size="XS" />
}

function setsAreEqual<T>(first: Set<T>, second: Set<T>): boolean {
  if (first.size !== second.size) return false
  for (const value of first) {
    if (!second.has(value)) return false
  }
  return true
}

export function shouldShowTripListRowMetadata(group: TripListGroup, rowIndex: number): boolean {
  return group.rowMetadataDisplay === 'all' || rowIndex === 0
}

export function buildTripListRenderableGroups(
  groups: Array<TripListGroup>,
  selectedRowIds: Set<string>,
  bookingRowId: string | undefined,
  noteUpdateRowId: string | undefined,
  statusUpdateRowId: string | undefined,
): Array<TripListRenderableGroup> {
  const firstGroupId = groups[0]?.id
  const lastGroupId = groups[groups.length - 1]?.id

  return groups.map(group => ({
    id: group.id,
    rows: group.rows.map((row, rowIndex) => ({
      row,
      showGroupMetadata: shouldShowTripListRowMetadata(group, rowIndex),
      firstRow: firstGroupId === group.id && rowIndex === 0,
      lastRow: lastGroupId === group.id && rowIndex === group.rows.length - 1,
      selected: selectedRowIds.has(row.id),
      isBookingPending: bookingRowId === row.id,
      isNoteUpdatePending: noteUpdateRowId === row.id,
      isStatusUpdatePending: statusUpdateRowId === row.id,
    })),
  }))
}

function TripListView({
  groups,
  tripStops,
  allRows,
  visibleRows,
  selectedRows,
  selectedRowIds,
  filters,
  sort,
  luxPlusEnabled,
  onSelectAll,
  onSelectRow,
  onLocationFiltersChange,
  onTypeFiltersChange,
  onStatusFiltersChange,
  onClearFilters,
  onSortToggle,
  onLuxPlusToggle,
  onRowClick,
  onBookRow,
  onBookAll,
  onViewBooking,
  onAskAi,
  onAskAiMove,
  onAskAiSelectedRows,
  onAskAiMoveSelectedRows,
  onDelete,
  onMoveToDay,
  onMoveSelectedRowsToDay,
  onDeleteSelectedRows,
  onClearSelection,
  onUpdateNote,
  onUpdateStatus,
  bookingRowId,
  noteUpdateRowId,
  statusUpdateRowId,
  notesDisabled = false,
  statusDisabled = false,
  actionsDisabled = false,
  scrollTarget,
  onScrollComplete,
}: Props) {
  const intl = useIntl()
  const [locationFilterOpen, setLocationFilterOpen] = useState(false)
  const [typeFilterOpen, setTypeFilterOpen] = useState(false)
  const [statusFilterOpen, setStatusFilterOpen] = useState(false)
  const [headerStuck, setHeaderStuck] = useState(false)
  const [pendingLocationFilters, setPendingLocationFilters] = useState<Set<string>>(() => new Set(filters.locations))
  const [pendingTypeFilters, setPendingTypeFilters] = useState<Set<TripListRowType>>(() => new Set(filters.types))
  const [pendingStatusFilters, setPendingStatusFilters] = useState<Set<TripListStatusValue>>(() => new Set(filters.statuses))
  const [pendingBookableOnly, setPendingBookableOnly] = useState(filters.bookableOnly)
  const locationFilterTriggerRef = useRef<HTMLButtonElement>(null)
  const typeFilterTriggerRef = useRef<HTMLButtonElement>(null)
  const statusFilterTriggerRef = useRef<HTMLButtonElement>(null)
  const scrollAreaRef = useRef<HTMLDivElement>(null)

  useItineraryScrollTarget({
    scrollTarget,
    containerRef: scrollAreaRef,
    selectors: TRIP_LIST_SCROLL_SELECTORS,
    onScrollComplete,
  })

  const bookableRows = useMemo(() => visibleRows.filter(row => row.isBookable && !row.isBooked), [visibleRows])
  const footerTotal = useMemo(() => getTripListFooterTotalPrice(bookableRows.map(row => row.price), luxPlusEnabled), [bookableRows, luxPlusEnabled])
  const renderableGroups = useMemo(
    () => buildTripListRenderableGroups(groups, selectedRowIds, bookingRowId, noteUpdateRowId, statusUpdateRowId),
    [bookingRowId, groups, noteUpdateRowId, selectedRowIds, statusUpdateRowId],
  )
  const selectedRowsCanMoveToDay = selectedRows.length > 0 && selectedRows.every(canMoveTripListRowToAnotherDay)
  const selectedMoveDayOptions = useMemo(
    () => selectedRowsCanMoveToDay ? getTripListBulkMoveDayOptions(selectedRows, tripStops, intl) : [],
    [intl, selectedRows, selectedRowsCanMoveToDay, tripStops],
  )
  const showSelectedMoveWithAi = selectedRows.length > 0 && !selectedRowsCanMoveToDay
  const allRowsSelected = visibleRows.length > 0 && selectedRows.length === visibleRows.length
  const hasPartialSelection = selectedRows.length > 0 && !allRowsSelected
  const activeLocationFilterCount = filters.locations.size
  const hasActiveLocationFilter = activeLocationFilterCount > 0
  const activeTypeFilterCount = filters.types.size + (filters.bookableOnly ? 1 : 0)
  const hasActiveTypeFilter = activeTypeFilterCount > 0
  const activeStatusFilterCount = filters.statuses.size
  const hasActiveStatusFilter = activeStatusFilterCount > 0
  const hasActiveSort = Boolean(sort)
  const hasActiveFilters = hasActiveLocationFilter || hasActiveTypeFilter || hasActiveStatusFilter || filters.bookableOnly || hasActiveSort
  const hasPendingLocationFilterChanges = !setsAreEqual(pendingLocationFilters, filters.locations)
  const hasPendingTypeFilterChanges = !setsAreEqual(pendingTypeFilters, filters.types) || pendingBookableOnly !== filters.bookableOnly
  const hasPendingStatusFilterChanges = !setsAreEqual(pendingStatusFilters, filters.statuses)
  const locationFilterOptions = useMemo(() => {
    const options = new Map<string, LocationFilterOption>()

    allRows.forEach((row, index) => {
      if (row.kind !== 'item') return

      const existing = options.get(row.stopLocation.filterValue)
      if (existing) {
        existing.count += 1
        return
      }

      options.set(row.stopLocation.filterValue, {
        location: row.stopLocation,
        count: 1,
        firstIndex: index,
      })
    })

    return Array.from(options.values()).sort((a, b) => a.firstIndex - b.firstIndex)
  }, [allRows])
  const rowCountByType = useMemo(() => {
    return countByAsRecord(allRows, row => row.rowType)
  }, [allRows])
  const typeFilterOptions = useMemo(() => {
    return TYPE_FILTERS.filter(type => (rowCountByType[type] ?? 0) > 0)
  }, [rowCountByType])
  const rowCountByStatus = useMemo(() => {
    return countByAsRecord(allRows, row => row.status.value)
  }, [allRows])
  const statusFilterOptions = useMemo(() => {
    return STATUS_FILTERS.filter(status => (rowCountByStatus[status] ?? 0) > 0)
  }, [rowCountByStatus])

  const handleSelectAllChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    onSelectAll(event.currentTarget.checked)
  }, [onSelectAll])

  const handleSelectAllCellClick = useCallback(() => {
    onSelectAll(!allRowsSelected)
  }, [allRowsSelected, onSelectAll])

  const handleCheckboxClick = useCallback((event: MouseEvent) => {
    event.stopPropagation()
  }, [])

  const handlePriceSortClick = useCallback(() => {
    onSortToggle('price')
  }, [onSortToggle])

  const toggleLocationFilter = useCallback(() => {
    if (!locationFilterOpen) {
      setPendingLocationFilters(new Set(filters.locations))
    }
    setLocationFilterOpen(!locationFilterOpen)
  }, [filters.locations, locationFilterOpen])

  const closeLocationFilter = useCallback(() => {
    setLocationFilterOpen(false)
  }, [])

  const handlePendingLocationFilterToggle = useCallback((location: string) => {
    setPendingLocationFilters(prev => {
      const next = new Set(prev)
      if (next.has(location)) {
        next.delete(location)
      } else {
        next.add(location)
      }
      return next
    })
  }, [])

  const handleApplyLocationFilters = useCallback(() => {
    onLocationFiltersChange(pendingLocationFilters)
    closeLocationFilter()
  }, [closeLocationFilter, onLocationFiltersChange, pendingLocationFilters])

  const handleClearLocationFilters = useCallback(() => {
    setPendingLocationFilters(new Set())
  }, [])

  const toggleTypeFilter = useCallback(() => {
    if (!typeFilterOpen) {
      setPendingTypeFilters(new Set(filters.types))
      setPendingBookableOnly(filters.bookableOnly)
    }
    setTypeFilterOpen(!typeFilterOpen)
  }, [filters.bookableOnly, filters.types, typeFilterOpen])

  const closeTypeFilter = useCallback(() => {
    setTypeFilterOpen(false)
  }, [])

  const handlePendingTypeFilterToggle = useCallback((type: TripListRowType) => {
    setPendingTypeFilters(prev => {
      const next = new Set(prev)
      if (next.has(type)) {
        next.delete(type)
      } else {
        next.add(type)
      }
      return next
    })
  }, [])

  const handlePendingBookableOnlyToggle = useCallback(() => {
    setPendingBookableOnly(prev => !prev)
  }, [])

  const handleApplyTypeFilters = useCallback(() => {
    onTypeFiltersChange(pendingTypeFilters, pendingBookableOnly)
    closeTypeFilter()
  }, [closeTypeFilter, onTypeFiltersChange, pendingBookableOnly, pendingTypeFilters])

  const handleClearTypeFilters = useCallback(() => {
    setPendingTypeFilters(new Set())
    setPendingBookableOnly(false)
  }, [])

  const toggleStatusFilter = useCallback(() => {
    if (!statusFilterOpen) {
      setPendingStatusFilters(new Set(filters.statuses))
    }
    setStatusFilterOpen(!statusFilterOpen)
  }, [filters.statuses, statusFilterOpen])

  const closeStatusFilter = useCallback(() => {
    setStatusFilterOpen(false)
  }, [])

  const handlePendingStatusFilterToggle = useCallback((status: TripListStatusValue) => {
    setPendingStatusFilters(prev => {
      const next = new Set(prev)
      if (next.has(status)) {
        next.delete(status)
      } else {
        next.add(status)
      }
      return next
    })
  }, [])

  const handleApplyStatusFilters = useCallback(() => {
    onStatusFiltersChange(pendingStatusFilters)
    closeStatusFilter()
  }, [closeStatusFilter, onStatusFiltersChange, pendingStatusFilters])

  const handleClearStatusFilters = useCallback(() => {
    setPendingStatusFilters(new Set())
  }, [])

  const handleClearAllFilters = useCallback(() => {
    setLocationFilterOpen(false)
    setTypeFilterOpen(false)
    setStatusFilterOpen(false)
    setPendingLocationFilters(new Set())
    setPendingTypeFilters(new Set())
    setPendingStatusFilters(new Set())
    setPendingBookableOnly(false)
    onClearFilters()
  }, [onClearFilters])

  const handleScroll = useCallback((event: UIEvent<HTMLDivElement>) => {
    const nextHeaderStuck = event.currentTarget.scrollTop >= HEADER_STICKY_SCROLL_THRESHOLD
    setHeaderStuck(prev => prev === nextHeaderStuck ? prev : nextHeaderStuck)
  }, [])

  const renderTotalAmount = useCallback((chunks: ReactNode) => (
    <BodyText as="strong" variant="large" weight="bold">{chunks}</BodyText>
  ), [])

  const typeLabelByType = useMemo<Record<TripListRowType, string>>(() => ({
    accommodation: getTripListTypeLabel(intl, 'accommodation'),
    experience: getTripListTypeLabel(intl, 'experience'),
    dining: getTripListTypeLabel(intl, 'dining'),
    flight: getTripListTypeLabel(intl, 'flight'),
    transport: getTripListTypeLabel(intl, 'transport'),
    cruise: getTripListTypeLabel(intl, 'cruise'),
    activity: getTripListTypeLabel(intl, 'activity'),
  }), [intl])

  const typeHeaderLabel = intl.formatMessage({
    id: 'RTLU_2os7TlW4Bu0fGMO',
    defaultMessage: 'Type',
    description: 'Type column header label in the trip planner list view',
  })

  return (
    <ListPanel>
      <ScrollArea ref={scrollAreaRef} onScroll={handleScroll}>
        <Toolbar>
          <ToolbarControls>
            {hasActiveFilters && (
              <ClearFiltersButton
                size="medium"
                kind="secondary"
                startIcon={<LineTimesIcon size="S" />}
                onClick={handleClearAllFilters}
              >
                {intl.formatMessage({
                  id: 'BEzjrYXQeqoRk_KikB7D',
                  defaultMessage: 'Clear filters',
                  description: 'Menu item label for clearing list view filters in the trip planner list view',
                })}
              </ClearFiltersButton>
            )}
            <LuxPlusToggle>
              <LuxPlusLogoWrapper height={10} />
              <ProductPaletteProvider palette="lux-plus">
                <Switch checked={luxPlusEnabled} onChange={onLuxPlusToggle} />
              </ProductPaletteProvider>
            </LuxPlusToggle>
          </ToolbarControls>
        </Toolbar>
        <HeaderRow className={headerStuck ? 'is-stuck' : undefined}>
          <HeaderCheckboxCell onClick={handleSelectAllCellClick}>
            <TripListCheckboxButton
              size="small"
              className="is-table-checkbox"
              checked={allRowsSelected}
              indeterminate={hasPartialSelection}
              onChange={handleSelectAllChange}
              onClick={handleCheckboxClick}
              aria-label={intl.formatMessage({
                id: 'ZeEQT3x-Y6zCUOXzT_ke',
                defaultMessage: 'Select all itinerary items',
                description: 'Accessible label for selecting all visible rows in the trip planner list view',
              })}
            />
          </HeaderCheckboxCell>
          <HeaderCell>
            <BodyText variant="small" weight="bold">
              {intl.formatMessage({
                id: 'QT5QhUHshZObnja-2LmH',
                defaultMessage: 'Day',
                description: 'Day column header label in the trip planner list view',
              })}
            </BodyText>
          </HeaderCell>
          <HeaderCell>
            <BodyText variant="small" weight="bold">
              {intl.formatMessage({
                id: 'X3S-4N1Qdxx3efgejC7X',
                defaultMessage: 'Location',
                description: 'Location column header label in the trip planner list view',
              })}
            </BodyText>
            <FilterTriggerButton
              type="button"
              kind="secondary"
              onClick={toggleLocationFilter}
              ref={locationFilterTriggerRef}
              className={hasActiveLocationFilter ? 'is-active' : undefined}
            >
              {hasActiveLocationFilter && <Caption variant="medium" weight="bold">{activeLocationFilterCount}</Caption>}
              <LineAngleDownIcon size="XS" />
            </FilterTriggerButton>
            <FilterDropdownList
              size="S"
              open={locationFilterOpen}
              onClose={closeLocationFilter}
              triggerRef={locationFilterTriggerRef}
              anchorRef={locationFilterTriggerRef}
              placement="bottom-start"
              portaled
            >
              <TypeFilterPanel>
                <TypeFilterHeader>
                  <BodyText variant="large" weight="bold">
                    {intl.formatMessage({
                      id: 'G9V19vN5wDaRVYwvFTJ4',
                      defaultMessage: 'Filter',
                      description: 'Heading for the trip planner list type filter menu',
                    })}
                  </BodyText>
                </TypeFilterHeader>
                <TypeFilterContent>
                  <TypeFilterOptions>
                    {locationFilterOptions.map(({ location, count }) => (
                      <LocationFilterMenuItem
                        key={location.filterValue}
                        value={location.filterValue}
                        label={location.label}
                        count={count}
                        selected={pendingLocationFilters.has(location.filterValue)}
                        onToggle={handlePendingLocationFilterToggle}
                      />
                    ))}
                  </TypeFilterOptions>
                </TypeFilterContent>
                <TypeFilterFooter>
                  <ClearFilterButton size="medium" kind="tertiary" onClick={handleClearLocationFilters}>
                    {intl.formatMessage({
                      id: 'lNyUskODo7jk9JmaBXfr',
                      defaultMessage: 'Clear',
                      description: 'Button label for clearing trip planner list type filters before applying',
                    })}
                  </ClearFilterButton>
                  <TextButton
                    size="medium"
                    kind="primary"
                    disabled={!hasPendingLocationFilterChanges}
                    onClick={handleApplyLocationFilters}
                  >
                    {intl.formatMessage({
                      id: 'e9qjVAlU7YvycNW29ftc',
                      defaultMessage: 'Apply',
                      description: 'Button label for applying trip planner list type filters',
                    })}
                  </TextButton>
                </TypeFilterFooter>
              </TypeFilterPanel>
            </FilterDropdownList>
          </HeaderCell>
          <HeaderCell>
            <BodyText variant="small" weight="bold">{typeHeaderLabel}</BodyText>
            <FilterTriggerButton
              type="button"
              kind="secondary"
              onClick={toggleTypeFilter}
              ref={typeFilterTriggerRef}
              className={hasActiveTypeFilter ? 'is-active' : undefined}
            >
              {hasActiveTypeFilter && <Caption variant="medium" weight="bold">{activeTypeFilterCount}</Caption>}
              <LineAngleDownIcon size="XS" />
            </FilterTriggerButton>
            <FilterDropdownList
              size="S"
              open={typeFilterOpen}
              onClose={closeTypeFilter}
              triggerRef={typeFilterTriggerRef}
              anchorRef={typeFilterTriggerRef}
              placement="bottom-start"
              portaled
            >
              <TypeFilterPanel>
                <TypeFilterHeader>
                  <BodyText variant="large" weight="bold">
                    {intl.formatMessage({
                      id: 'G9V19vN5wDaRVYwvFTJ4',
                      defaultMessage: 'Filter',
                      description: 'Heading for the trip planner list type filter menu',
                    })}
                  </BodyText>
                </TypeFilterHeader>
                <TypeFilterContent>
                  <TypeFilterOptions>
                    {typeFilterOptions.map(type => (
                      <TypeFilterMenuItem
                        key={type}
                        type={type}
                        label={typeLabelByType[type]}
                        count={rowCountByType[type]}
                        selected={pendingTypeFilters.has(type)}
                        onToggle={handlePendingTypeFilterToggle}
                      />
                    ))}
                    <LocationFilterMenuItem
                      value="bookableOnly"
                      label={intl.formatMessage({
                        id: 'Z0lGZ1R9f0cNfrErpwD4',
                        defaultMessage: 'Bookable only',
                        description: 'Filter option label for showing only bookable rows in the trip planner list view',
                      })}
                      count={bookableRows.length}
                      selected={pendingBookableOnly}
                      onToggle={handlePendingBookableOnlyToggle}
                    />
                  </TypeFilterOptions>
                </TypeFilterContent>
                <TypeFilterFooter>
                  <ClearFilterButton size="medium" kind="tertiary" onClick={handleClearTypeFilters}>
                    {intl.formatMessage({
                      id: 'lNyUskODo7jk9JmaBXfr',
                      defaultMessage: 'Clear',
                      description: 'Button label for clearing trip planner list type filters before applying',
                    })}
                  </ClearFilterButton>
                  <TextButton
                    size="medium"
                    kind="primary"
                    disabled={!hasPendingTypeFilterChanges}
                    onClick={handleApplyTypeFilters}
                  >
                    {intl.formatMessage({
                      id: 'e9qjVAlU7YvycNW29ftc',
                      defaultMessage: 'Apply',
                      description: 'Button label for applying trip planner list type filters',
                    })}
                  </TextButton>
                </TypeFilterFooter>
              </TypeFilterPanel>
            </FilterDropdownList>
          </HeaderCell>
          <HeaderCell>
            <BodyText variant="small" weight="bold">
              {intl.formatMessage({
                id: 'XQ5cqE6Sne1ahbljGP5b',
                defaultMessage: 'Notes',
                description: 'Notes column header label in the trip planner list view',
              })}
            </BodyText>
          </HeaderCell>
          <HeaderCell>
            <BodyText variant="small" weight="bold">
              {intl.formatMessage({
                id: 'BsOr1mxehY4HiiOzz7Jf',
                defaultMessage: 'Status',
                description: 'Status column header label in the trip planner list view',
              })}
            </BodyText>
            <FilterTriggerButton
              type="button"
              kind="secondary"
              onClick={toggleStatusFilter}
              ref={statusFilterTriggerRef}
              className={hasActiveStatusFilter ? 'is-active' : undefined}
            >
              {hasActiveStatusFilter && <Caption variant="medium" weight="bold">{activeStatusFilterCount}</Caption>}
              <LineAngleDownIcon size="XS" />
            </FilterTriggerButton>
            <FilterDropdownList
              size="S"
              open={statusFilterOpen}
              onClose={closeStatusFilter}
              triggerRef={statusFilterTriggerRef}
              anchorRef={statusFilterTriggerRef}
              placement="bottom-start"
              portaled
            >
              <TypeFilterPanel>
                <TypeFilterHeader>
                  <BodyText variant="large" weight="bold">
                    {intl.formatMessage({
                      id: 'G9V19vN5wDaRVYwvFTJ4',
                      defaultMessage: 'Filter',
                      description: 'Heading for the trip planner list type filter menu',
                    })}
                  </BodyText>
                </TypeFilterHeader>
                <TypeFilterContent>
                  <TypeFilterOptions>
                    {statusFilterOptions.map(status => (
                      <StatusFilterMenuItem
                        key={status}
                        status={status}
                        label={getTripListStatusLabel(intl, status)}
                        count={rowCountByStatus[status]}
                        selected={pendingStatusFilters.has(status)}
                        onToggle={handlePendingStatusFilterToggle}
                      />
                    ))}
                  </TypeFilterOptions>
                </TypeFilterContent>
                <TypeFilterFooter>
                  <ClearFilterButton size="medium" kind="tertiary" onClick={handleClearStatusFilters}>
                    {intl.formatMessage({
                      id: 'lNyUskODo7jk9JmaBXfr',
                      defaultMessage: 'Clear',
                      description: 'Button label for clearing trip planner list type filters before applying',
                    })}
                  </ClearFilterButton>
                  <TextButton
                    size="medium"
                    kind="primary"
                    disabled={!hasPendingStatusFilterChanges}
                    onClick={handleApplyStatusFilters}
                  >
                    {intl.formatMessage({
                      id: 'e9qjVAlU7YvycNW29ftc',
                      defaultMessage: 'Apply',
                      description: 'Button label for applying trip planner list type filters',
                    })}
                  </TextButton>
                </TypeFilterFooter>
              </TypeFilterPanel>
            </FilterDropdownList>
          </HeaderCell>
          <HeaderCell>
            <BodyText variant="small" weight="bold">
              {intl.formatMessage({
                id: 'ChJyqLBRZb8uxapP1Izz',
                defaultMessage: 'Price',
                description: 'Price column header label in the trip planner list view',
              })}
            </BodyText>
            <HeaderButton
              type="button"
              kind="secondary"
              onClick={handlePriceSortClick}
              aria-label={intl.formatMessage({
                id: 'Nh7mkoTbbVKMqi-doe60',
                defaultMessage: 'Sort by price',
                description: 'Accessible label for the trip planner list view price column sort button',
              })}
            >
              {getSortIcon(sort, 'price')}
            </HeaderButton>
          </HeaderCell>
          <HeaderCell />
        </HeaderRow>
        {groups.length === 0 && (
          <EmptyState>
            <BodyText variant="medium" colour="light-foreground-alt">
              {intl.formatMessage({
                id: '_HhYADAgKgwTYQp743JX',
                defaultMessage: 'No itinerary items match these filters.',
                description: 'Empty state shown when the trip planner list view has no rows after filtering',
              })}
            </BodyText>
          </EmptyState>
        )}
        {groups.length > 0 && (
          <ContentStack>
            {renderableGroups.map(group => (
              <DayGroup key={group.id}>
                {group.rows.map(rowView => (
                  <TripListRowItem
                    key={rowView.row.id}
                    row={rowView.row}
                    tripStops={tripStops}
                    selected={rowView.selected}
                    showGroupMetadata={rowView.showGroupMetadata}
                    firstRow={rowView.firstRow}
                    lastRow={rowView.lastRow}
                    luxPlusEnabled={luxPlusEnabled}
                    onSelectRow={onSelectRow}
                    onRowClick={onRowClick}
                    onBookRow={onBookRow}
                    onViewBooking={onViewBooking}
                    onAskAi={onAskAi}
                    onAskAiMove={onAskAiMove}
                    onDelete={onDelete}
                    onMoveToDay={onMoveToDay}
                    onUpdateNote={onUpdateNote}
                    onUpdateStatus={onUpdateStatus}
                    isBookingPending={rowView.isBookingPending}
                    isNoteUpdatePending={rowView.isNoteUpdatePending}
                    isStatusUpdatePending={rowView.isStatusUpdatePending}
                    notesDisabled={notesDisabled}
                    statusDisabled={statusDisabled}
                    actionsDisabled={actionsDisabled}
                  />
                ))}
              </DayGroup>
            ))}
          </ContentStack>
        )}
      </ScrollArea>
      {selectedRows.length > 0 && (
        <TripListQuickActions
          selectedCount={selectedRows.length}
          disabled={actionsDisabled}
          moveDayOptions={selectedMoveDayOptions}
          showMoveWithAi={showSelectedMoveWithAi}
          onAskAi={onAskAiSelectedRows}
          onMoveToDay={onMoveSelectedRowsToDay}
          onMoveWithAi={onAskAiMoveSelectedRows}
          onDelete={onDeleteSelectedRows}
          onClear={onClearSelection}
        />
      )}
      {bookableRows.length > 0 && (
        <Footer>
          <FooterSummary direction="horizontal" gap={8} verticalAlign="center">
            <BodyText variant="medium" weight="bold">
              {intl.formatMessage({
                id: '3hjcizN16s5NKoi8v_PR',
                defaultMessage: 'Book through Luxury Escapes',
                description: 'Footer label describing rows that can be booked through Luxury Escapes',
              })}
            </BodyText>
            <CountChip>
              <Caption variant="large">
                {intl.formatMessage(
                  {
                    id: 'TtDyDrOLvud-zyDhBmK8',
                    defaultMessage: '{count, plural, one {# item} other {# items}}',
                    description: 'Footer count of bookable itinerary items in the trip planner list view',
                  },
                  { count: bookableRows.length },
                )}
              </Caption>
            </CountChip>
          </FooterSummary>
          <FooterActions direction="horizontal" gap={16} verticalAlign="center">
            {selectedRows.length > 0 && (
              <BodyText variant="medium" colour="light-foreground-alt">
                {intl.formatMessage(
                  {
                    id: 'KN0QKCcZyMWKUhcPSWOa',
                    defaultMessage: '{count, plural, =0 {No items selected} one {# item selected} other {# items selected}}',
                    description: 'Selected item count shown in the trip planner list footer',
                  },
                  { count: selectedRows.length },
                )}
              </BodyText>
            )}
            {footerTotal && (
              <BodyText variant="medium" colour="normal-contrast">
                {intl.formatMessage(
                  {
                    id: '3dVgNkGJWa2aJcRksMWS',
                    defaultMessage: 'Total <amount>{amountValue}</amount>',
                    description: 'Total selected row price shown in the trip planner list footer',
                  },
                  {
                    amountValue: <FormatCurrency value={footerTotal.total} currency={footerTotal.currency} />,
                    amount: renderTotalAmount,
                  },
                )}
              </BodyText>
            )}
            <BookAllButton size="medium" kind="primary" disabled={actionsDisabled} onClick={onBookAll}>
              {intl.formatMessage({
                id: 'pRxQObCdrNpxcYUl2ccY',
                defaultMessage: 'Book all',
                description: 'CTA button label for booking selected rows in the trip planner list view',
              })}
            </BookAllButton>
          </FooterActions>
        </Footer>
      )}
    </ListPanel>
  )
}

export default TripListView
